import {
  DashboardConversationFeedItemType,
  type DashboardConversationFeedItem,
  type DashboardMessageFeedItem,
  type PaginationMeta,
} from '@fluenceur/types';
import { useInfiniteQuery, type InfiniteData } from '@tanstack/react-query';
import api from '@/libs/axiosInstance';
import { conversationQueryKeys } from './conversation-query-keys';

interface ConversationFeedGroup {
  date: string;
  items: DashboardConversationFeedItem[];
}

export interface ConversationMessagesResponse {
  data: DashboardConversationFeedItem[];
  meta: PaginationMeta;
}

export const DEFAULT_CONVERSATION_MESSAGES_LIMIT = 25;

export type ConversationMessagesInfiniteData = InfiniteData<ConversationMessagesResponse, number>;

export const conversationMessagesQueryKey = (
  brandId: string,
  conversationId: string,
  params?: { limit?: number }
) =>
  conversationQueryKeys.messages(
    brandId,
    conversationId,
    params?.limit ?? DEFAULT_CONVERSATION_MESSAGES_LIMIT
  );

const fetchConversationMessages = async (
  conversationId: string,
  params?: { page?: number; limit?: number }
): Promise<ConversationMessagesResponse> => {
  const response = await api.get(`/dashboard/conversations/${conversationId}/messages`, {
    params,
  });
  return response.data;
};

export const useConversationMessagesQuery = (
  brandId: string | null | undefined,
  conversationId: string,
  params?: { page?: number; limit?: number; enabled?: boolean }
) => {
  const limit = params?.limit ?? DEFAULT_CONVERSATION_MESSAGES_LIMIT;

  return useInfiniteQuery<ConversationMessagesResponse, Error>({
    queryKey: conversationMessagesQueryKey(brandId ?? '', conversationId, { limit }),
    queryFn: ({ pageParam }) =>
      fetchConversationMessages(conversationId, {
        page: pageParam as number,
        limit,
      }),
    initialPageParam: params?.page ?? 1,
    getNextPageParam: (lastPage) => {
      const { currentPage, lastPage: lastPageNumber } = lastPage.meta;
      return currentPage < lastPageNumber ? currentPage + 1 : undefined;
    },
    enabled: !!brandId && !!conversationId && (params?.enabled ?? true),
    gcTime: 1000 * 60 * 5, // 5 minutes
    staleTime: 1000 * 30, // 30 seconds
    retry: 2,
  });
};

export const getChronologicalMessages = (
  pages: ConversationMessagesResponse[] | undefined
): DashboardMessageFeedItem[] =>
  getChronologicalConversationFeed(pages).filter(
    (item): item is DashboardMessageFeedItem =>
      item.itemType === DashboardConversationFeedItemType.MESSAGE
  );

export const getChronologicalConversationFeed = (
  pages: ConversationMessagesResponse[] | undefined
): DashboardConversationFeedItem[] => {
  if (!pages?.length) return [];

  const itemsById = new Map<string, DashboardConversationFeedItem>();

  pages
    .slice()
    .reverse()
    .forEach((page) => {
      page.data
        .slice()
        .reverse()
        .forEach((item) => {
          itemsById.set(`${item.itemType}:${item.id}`, item);
        });
    });

  return Array.from(itemsById.values()).sort(
    (a, b) =>
      new Date(a.occurredAt).getTime() - new Date(b.occurredAt).getTime() ||
      a.id.localeCompare(b.id)
  );
};

export const patchMessageMetadataInPages = (
  prev: ConversationMessagesInfiniteData | undefined,
  messageId: string,
  metadata: Record<string, unknown>
): { data: ConversationMessagesInfiniteData | undefined; patched: boolean } => {
  if (!prev) return { data: prev, patched: false };

  let patched = false;
  const pages = prev.pages.map((page) => {
    const idx = page.data.findIndex(
      (item) => item.itemType === DashboardConversationFeedItemType.MESSAGE && item.id === messageId
    );
    if (idx === -1) return page;

    const existing = page.data[idx];
    if (existing.itemType !== DashboardConversationFeedItemType.MESSAGE) return page;
    const nextData = [...page.data];
    nextData[idx] = {
      ...existing,
      metadata: { ...(existing.metadata ?? {}), ...metadata },
    };
    patched = true;
    return { ...page, data: nextData };
  });

  return {
    data: patched ? { ...prev, pages } : prev,
    patched,
  };
};

// Helper function to group messages by date
export const groupConversationFeedByDate = (
  items: DashboardConversationFeedItem[]
): ConversationFeedGroup[] => {
  const groups: Record<string, DashboardConversationFeedItem[]> = {};

  items.forEach((item) => {
    const date = new Date(item.occurredAt).toDateString();
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(item);
  });

  return Object.entries(groups)
    .map(([date, groupedItems]) => ({
      date,
      items: groupedItems,
    }))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
};
